import {
	Button,
	ButtonGroup,
	type CloseDialog,
	Dialog,
	type DialogProps,
	IconButton,
	Input,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
} from "@overmap-ai/blocks"
import React, { forwardRef, memo, type PropsWithoutRef, type ReactNode, useCallback, useState } from "react"

import { modelIconMapping } from "../../mappings/icons"

interface TextInputConfirmationDialogProps extends Omit<PropsWithoutRef<DialogProps>, "content"> {
	confirmString: string
	confirmMessage: string
	placeholder?: string
	description?: string
	onConfirm?: () => void
	actionLabel?: ReactNode
	cancelLabel?: ReactNode
	title: string
}

// TODO: create a context for this so that is can be called similarily to an alert Dialog
export const TextConfirmationDialog = memo(
	forwardRef<HTMLDivElement, TextInputConfirmationDialogProps>((props, ref) => {
		const {
			children,
			title,
			description,
			confirmString,
			confirmMessage,
			placeholder,
			onConfirm,
			actionLabel,
			cancelLabel,
			...rest
		} = props

		const [textValue, setTextValue] = useState("")

		const textValueMatchesConfirmationString = textValue.toLowerCase() === confirmString.toLowerCase()

		const handleTextInputOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
			setTextValue(event.target.value)
		}, [])

		const callbackWithClose = useCallback(
			(close: CloseDialog, callback?: () => void) => () => {
				if (callback) callback()
				setTextValue("")
				close()
			},
			[],
		)

		return (
			<Dialog.Root {...rest}>
				{children ? <Dialog.Trigger asChild>{children}</Dialog.Trigger> : null}
				<Dialog.Overlay onClick={stopPropagation} />
				<Dialog.Content title={title} ref={ref} onClick={stopPropagation} size="sm">
					{(close) => (
						<>
							<div className="flex justify-between gap-2 pb-2">
								<Dialog.Title>
									<Text size="lg">{title}</Text>
								</Dialog.Title>

								<Dialog.Close asChild>
									<IconButton variant="ghost" accentColor="base">
										<LuIcon icon={modelIconMapping.close} />
									</IconButton>
								</Dialog.Close>
							</div>
							<div className="flex flex-col gap-4">
								{!!description && (
									<>
										<Text aria-label="input-description" size="sm" accentColor="base">
											{description}
										</Text>
										<Separator size="full" />
									</>
								)}

								<div className="flex flex-col gap-4">
									<div className="flex flex-col gap-1">
										<Text aria-label="input-description" size="sm" accentColor="base">
											{confirmMessage}
										</Text>
										<Input.Root variant="outline">
											<Input.Field
												placeholder={placeholder}
												value={textValue}
												onChange={handleTextInputOnChange}
											/>
											<Input.Slot>
												{textValueMatchesConfirmationString ? (
													<LuIcon icon={modelIconMapping.check} />
												) : undefined}
											</Input.Slot>
										</Input.Root>
									</div>
									<ButtonGroup className="flex items-center justify-end gap-2" size="sm">
										<Button onClick={callbackWithClose(close)} accentColor="base" variant="soft">
											<LuIcon icon={modelIconMapping.close} />
											{cancelLabel ? cancelLabel : "Cancel"}
										</Button>
										<Button
											onClick={callbackWithClose(close, onConfirm)}
											disabled={!textValueMatchesConfirmationString}
											accentColor="danger"
											variant="solid"
										>
											<LuIcon icon={modelIconMapping.delete} />
											{actionLabel ? actionLabel : "Yes"}
										</Button>
									</ButtonGroup>
								</div>
							</div>
						</>
					)}
				</Dialog.Content>
			</Dialog.Root>
		)
	}),
)
